import React, { FC, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import loadable from "@loadable/component";
import { currentDialogSelector, dialogsState } from "ducks/dialogs/selectors";
import { setDialogResources } from "ducks/dialogs/dialogs";

import { AddBookmark } from "./AddBookmark/AddBookmark";
import { AddByLink } from "./AddByLink/AddByLink";
import { ClassesRangeCalculation } from "./Classification/ClassesRangeCalculation";
import { ClassificationTitle } from "./Classification/ClassificationTitle";
import { CloseFeatureCard } from "./CloseFeatureCard/CloseFeatureCard";
import { Confirmation } from "./Confirmation/Confirmation";
import { DIALOGS } from "./constants";
import { DialogInfoContext } from "./context";
import { CopyFeature } from "./CopyFeature/CopyFeature";
import { DeleteFeature } from "./DeleteFeature/DeleteFeature";
import { DeleteLayer } from "./DeleteLayer/DeleteLayer";
import { DeleteGroup } from "./DeleteGroup/DeleteGroup";
import { LimitsExceeded } from "./LimitsExceeded/LimitsExceeded";
import { ScaleRestriction } from "./ScaleRestriction/ScaleRestriction";
import { SizeRanges } from "./Classification/SizeRanges";
import { AddImageAttribute } from "./AddImageAttribute/AddImageAttribute";
import { CalculatedAttributesVertex } from "./CalculatedAttributesVertex/CalculatedAttributesVertex";
import { ProjectAccess } from "./Access/ProjectAccess/ProjectAccess";
import { LayerAccess } from "./Access/LayerAccess/LayerAccess";
import { TableAccess } from "./Access/TableAccess/TableAccess";
import { WmsLink } from "./WmsLink/WmsLink";
import { ScaleSegmentation } from "./ScaleSegmentation/ScaleSegmentation";
import { ClusterSizeRanges } from "../SymbolEditor/components/ClusterSymbolEditor/ClusterSizeRanges";
import { LayerFolder } from "./LayerFolder/LayerFolder";
import { AddRelatedLayer } from "./AddRelatedLayer/AddRelatedLayer";
import { ConfigFilter } from "./ConfigFilter/ConfigFilter";
import { ConfigLayer } from "./ConfigLayer/ConfigLayer";

import * as Fallbacks from "./Fallbacks";

type CreateDialog = {
  path: string;
  fallback?: ReactElement;
};

const lazyDialog = ({ path, fallback }: CreateDialog) =>
  loadable(() => import(`../../components/Dialogs/${path}/${path}.module`), { fallback });

const preloadDialog = (createDialogParams: CreateDialog) => {
  const module = lazyDialog(createDialogParams);

  module.preload();

  return module;
};

const UploadData = preloadDialog({ path: "UploadData", fallback: <Fallbacks.UploadData /> });

const NewLayer = preloadDialog({ path: "NewLayer", fallback: <Fallbacks.NewLayer /> });

const AddTmsService = preloadDialog({ path: "AddTmsService", fallback: <Fallbacks.AddTmsService /> });

const EditResource = preloadDialog({ path: "EditResource", fallback: <Fallbacks.EditResource /> });

const CopyResource = preloadDialog({ path: "CopyResource", fallback: <Fallbacks.CopyResource /> });

const Import = preloadDialog({ path: "Import", fallback: <Fallbacks.Import /> });

const Export = preloadDialog({ path: "Export", fallback: <Fallbacks.Export /> });

const InfoResource = preloadDialog({ path: "InfoResource", fallback: <Fallbacks.InfoResource /> });

const NewProject = lazyDialog({ path: "NewProject", fallback: <Fallbacks.NewProject /> });

const FeaturesMultiEdit = preloadDialog({ path: "FeaturesMultiEdit", fallback: <Fallbacks.FeaturesMultiEdit /> });

const AddProxyService = preloadDialog({ path: "AddProxyService", fallback: <Fallbacks.AddProxyService /> });

const DeleteResources = lazyDialog({
  path: "DeleteResources",
  fallback: <Fallbacks.DeleteResources />,
});

const BulkCopy = lazyDialog({ path: "BulkCopy", fallback: <Fallbacks.BulkCopy /> });

const AvailabilityArea = lazyDialog({
  path: "AvailabilityArea",
  fallback: <Fallbacks.AvailabilityArea />,
});

const BufferZones = lazyDialog({ path: "BufferZones", fallback: <Fallbacks.BufferZones /> });

const GeometrySelection = lazyDialog({
  path: "GeometrySelection",
  fallback: <Fallbacks.GeometrySelection />,
});

const ShareProject = lazyDialog({ path: "ShareProject", fallback: <Fallbacks.ShareProject /> });

const NewLayerFromTable = lazyDialog({
  path: "NewLayerFromTable",
  fallback: <Fallbacks.NewLayerFromTable />,
});

const AggregationDialog = lazyDialog({
  path: "Aggregation",
  fallback: <Fallbacks.AggregationDialog />,
});

const GroundInfoDialog = lazyDialog({
  path: "GroundInfo",
  fallback: <Fallbacks.GroundInfo />,
});

const EditCoordinatesDialog = lazyDialog({
  path: "EditCoordinates",
  fallback: <Fallbacks.EditCoordinates />,
});

const BasemapSettingsDialog = lazyDialog({
  path: "BasemapSettings",
  fallback: <Fallbacks.BasemapSettings />,
});

const BasemapAddDialog = lazyDialog({
  path: "BasemapAdd",
  fallback: <Fallbacks.BasemapAdd />,
});

const TableReferencesDialog = lazyDialog({
  path: "TableReferences",
  fallback: <Fallbacks.TableReferences />,
});

const ProjectionChoiceDialog = lazyDialog({
  path: "ProjectionChoice",
  fallback: <Fallbacks.ProjectionChoice />,
});

const SelectResourcesDialog = lazyDialog({
  path: "SelectResources",
  fallback: <Fallbacks.SelectResources />,
});

const SelectFilesDialog = lazyDialog({
  path: "SelectFiles",
  fallback: <Fallbacks.SelectResources />,
});

const SaveQLSLayer = lazyDialog({
  path: "SaveQLSLayer",
  fallback: <Fallbacks.SaveQLSLayer />,
});

const ProjectInfo = lazyDialog({
  path: "ProjectInfo",
  fallback: <Fallbacks.SaveQLSLayer />,
});

const SaveTempSourceLayer = lazyDialog({
  path: "SaveTempSourceLayer",
  fallback: <Fallbacks.SaveQLSLayer />,
});

const SelectResourcePath = lazyDialog({
  path: "SelectResourcePath",
  fallback: <Fallbacks.SelectResourcePath />,
});

const DIALOGS_MAP: Record<DIALOGS, FC> = {
  [DIALOGS.UPLOAD_DATA]: UploadData,
  [DIALOGS.NEW_PROJECT]: NewProject,
  [DIALOGS.NEW_LAYER]: NewLayer,
  [DIALOGS.NEW_LAYER_FROM_TABLE]: NewLayerFromTable,
  [DIALOGS.ADD_TMS_SERVICE]: AddTmsService,
  [DIALOGS.DELETE_RESOURCES]: DeleteResources,
  [DIALOGS.EDIT_RESOURCE]: EditResource,
  [DIALOGS.COPY_RESOURCE]: CopyResource,
  [DIALOGS.COPY_CURRENT_PROJECT]: CopyResource,
  [DIALOGS.COPY_LAYER_FROM_PROJECT]: CopyResource,
  [DIALOGS.BULK_COPY_RESOURCE]: BulkCopy,
  [DIALOGS.IMPORT]: Import,
  [DIALOGS.EXPORT]: Export,
  [DIALOGS.AVAILABILITY_AREA]: AvailabilityArea,
  [DIALOGS.BUFFER]: BufferZones,
  [DIALOGS.GEOMETRY_SELECTION]: GeometrySelection,
  [DIALOGS.SHARE_DIALOG]: ShareProject,
  [DIALOGS.INFO_RESOURCE]: InfoResource,
  [DIALOGS.AGGREGATION]: AggregationDialog,
  [DIALOGS.FEATURES_MULTI_EDIT]: FeaturesMultiEdit,
  [DIALOGS.COPY_FEATURE]: CopyFeature,
  [DIALOGS.DELETE_FEATURE]: DeleteFeature,
  [DIALOGS.DELETE_LAYER]: DeleteLayer,
  [DIALOGS.DELETE_GROUP]: DeleteGroup,
  [DIALOGS.ADD_BOOKMARK]: AddBookmark,
  [DIALOGS.SIZE_RANGES]: SizeRanges,
  [DIALOGS.CLASSES_RANGE_CALCULATION]: ClassesRangeCalculation,
  [DIALOGS.CLASSIFICATION_TITLE]: ClassificationTitle,
  [DIALOGS.SCALE_RESTRICTION]: ScaleRestriction,
  [DIALOGS.LIMITS_EXCEEDED]: LimitsExceeded,
  [DIALOGS.LAYER_FOLDER]: LayerFolder,
  [DIALOGS.ADD_RELATED_LAYER]: AddRelatedLayer,
  [DIALOGS.CLOSE_FEATURE_CARD]: CloseFeatureCard,
  [DIALOGS.CONFIRM]: Confirmation,
  [DIALOGS.CONFIG_FILTER]: ConfigFilter,
  [DIALOGS.CONFIG_LAYER]: ConfigLayer,
  [DIALOGS.ADD_IMAGE_ATTRIBUTE]: AddImageAttribute,
  [DIALOGS.CALCULATED_ATTRIBUTES_VERTEX]: CalculatedAttributesVertex,
  [DIALOGS.GROUND_INFO]: GroundInfoDialog,
  [DIALOGS.ADD_PROXY_SERVICE]: AddProxyService,
  [DIALOGS.PROJECT_ACCESS]: ProjectAccess,
  [DIALOGS.LAYER_ACCESS]: LayerAccess,
  [DIALOGS.TABLE_ACCESS]: TableAccess,
  [DIALOGS.WMS_LINK]: WmsLink,
  [DIALOGS.SCALE_SEGMENTATION]: ScaleSegmentation,
  [DIALOGS.EDIT_COORDINATES]: EditCoordinatesDialog,
  [DIALOGS.BASEMAP_SETTINGS]: BasemapSettingsDialog,
  [DIALOGS.BASEMAP_ADD]: BasemapAddDialog,
  [DIALOGS.TABLE_REFERENCES]: TableReferencesDialog,
  [DIALOGS.CLUSTER_SIZE_RANGES]: ClusterSizeRanges,
  [DIALOGS.PROJECTION_CHOICE]: ProjectionChoiceDialog,
  [DIALOGS.SELECT_RESOURCES]: SelectResourcesDialog,
  [DIALOGS.SELECT_FILES]: SelectFilesDialog,
  [DIALOGS.ADD_BY_LINK]: AddByLink,
  [DIALOGS.SAVE_QLS_LAYER]: SaveQLSLayer,
  [DIALOGS.PROJECT_INFO]: ProjectInfo,
  [DIALOGS.SAVE_TEMP_SOURCE_LAYER]: SaveTempSourceLayer,
  [DIALOGS.SELECT_RESOURCE_PATH]: SelectResourcePath,
};

export const Dialogs: FC = () => {
  const currentDialog = useSelector(currentDialogSelector);
  const state = useSelector(dialogsState);
  const Dialog = DIALOGS_MAP[currentDialog];

  const dispatch = useDispatch();

  useEffect(() => {
    if (currentDialog !== state.type) {
      dispatch(setDialogResources({ resources: [], options: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.type, currentDialog]);

  return Dialog ? (
    <>
      <DialogInfoContext.Provider value={{ resources: state.resources, options: state.options, type: currentDialog }}>
        <Dialog />
      </DialogInfoContext.Provider>
      {state.child?.map(({ type, resources, options }) => {
        const DialogComponent = DIALOGS_MAP[type];

        return (
          <DialogInfoContext.Provider value={{ resources, options, type }}>
            <DialogComponent />
          </DialogInfoContext.Provider>
        );
      })}
    </>
  ) : null;
};
